/* In den Warenkorb, ohne die Seite zu verlassen.
 *
 * Die Kachel (`hs-produktkachel`) und die Produktseite (`hs-produkt`) haben
 * eigene `{% form 'product' %}`-Formulare. Nativ abgeschickt antwortet
 * Shopify auf /cart/add mit einer Weiterleitung auf /cart -- wer eine Dose
 * in den Warenkorb legte, stand danach im Warenkorb statt weiter im Shop.
 *
 * Hier geht das Formular per /cart/add.js raus. Danach wird Horizons
 * `CartLinesUpdateEvent` ausgeloest: daran haengen der Zaehler im Kopf und
 * der Inhalt des Warenkorb-Drawers. Der Drawer oeffnet sich davon nur, wenn
 * „Warenkorb automatisch oeffnen" in den Theme-Einstellungen an ist -- das
 * ist bewusst aus. Rueckmeldung gibt der Knopf selbst.
 *
 * Ohne JavaScript bleibt das Formular ein normales Formular und fuehrt wie
 * bisher in den Warenkorb.
 *
 * Delegiert auf dem Dokument: das Raster der Kollektion wird beim Filtern
 * neu gezeichnet, einzeln gebundene Listener waeren danach weg.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const FORMULARE = 'form.hs-kachel__form, form.hs-pdp__form';
const RUECKMELDUNG_MS = 2000;

// Shopifys Bot-Pruefung (Cloudflare „Managed Challenge", `cf-mitigated:
// challenge`) beantwortet Warenkorb-Aufrufe in den ersten Sekunden einer
// Sitzung -- bei schlechtem IP-Ruf auch spaeter -- mit HTTP 429 und einer
// HTML-Seite „Verifying your connection...". `r.json()` scheiterte daran, und
// der Knopf zeigte „Fehler – bitte erneut". Gemessen 22.10.2026: der Artikel
// ist nach so einem 429 NICHT im Warenkorb, 4–6 s spaeter laeuft derselbe
// Aufruf mit 200 durch. Darum bei 429 nachfassen -- doppelt hinein kann dabei
// nichts, Shopify hat den abgewiesenen Aufruf nie verarbeitet.
const NACHFASSEN_MS = [1500, 3000, 5000];

const warte = (ms) => new Promise((w) => setTimeout(w, ms));

/**
 * /cart/add.js mit Nachfassen bei 429. Liefert Shopifys JSON-Antwort (auch
 * 422 mit `status`/`description`); wirft nur, wenn keine JSON-Antwort kommt.
 * Geht bewusst ueber `window.fetch`: G:Volume haengt dort `__mpb_offer_id`
 * an die Zeile, ohne das greift die Staffel nicht.
 */
async function hinzufuegen(daten, knopf) {
  for (let versuch = 0; ; versuch++) {
    const r = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: daten,
    });
    if (r.status === 429 && versuch < NACHFASSEN_MS.length) {
      if (knopf) knopf.textContent = 'Einen Moment …';
      await warte(NACHFASSEN_MS[versuch]);
      continue;
    }
    const text = await r.text();
    try {
      return JSON.parse(text);
    } catch {
      throw Object.assign(new Error(`cart/add: HTTP ${r.status}, keine JSON-Antwort`), { status: r.status });
    }
  }
}

function warenkorbLaden() {
  const komponente = document.querySelector('cart-items-component');
  if (komponente) {
    return customElements.whenDefined('cart-items-component').then(() => komponente.fetchCartData());
  }
  return fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((r) => r.json());
}

/** Menge dieser Variante im Warenkorb, null wenn der Warenkorb nicht lesbar ist. */
async function mengeImWarenkorb(variante) {
  try {
    const r = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const cart = await r.json();
    return cart.items.filter((z) => String(z.variant_id) === variante).reduce((s, z) => s + z.quantity, 0);
  } catch {
    return null;
  }
}

function knopfZeigen(knopf, text, fehler) {
  if (!knopf) return;
  if (!knopf.dataset.hsText) knopf.dataset.hsText = knopf.innerHTML;
  clearTimeout(Number(knopf.dataset.hsTimer));
  knopf.textContent = text;
  knopf.dataset.hsTimer = String(
    setTimeout(() => {
      knopf.innerHTML = knopf.dataset.hsText;
      knopf.disabled = false;
      knopf.removeAttribute('aria-busy');
    }, fehler ? RUECKMELDUNG_MS * 2 : RUECKMELDUNG_MS)
  );
}

document.addEventListener('submit', (ev) => {
  const form = ev.target instanceof HTMLFormElement ? ev.target.closest(FORMULARE) : null;
  // `Theme` steht in snippets/scripts.liquid als `const`, nicht an `window` --
  // `window.Theme` ist darum undefined, und das Formular ginge nativ raus.
  if (!form || !window.fetch || typeof Theme === 'undefined' || !Theme.routes) return;
  ev.preventDefault();

  const knopf = form.querySelector('button[type="submit"]');
  if (knopf?.disabled) return;
  if (knopf) {
    // Vor jedem Textwechsel sichern -- auch vor „Einen Moment …" beim Nachfassen.
    if (!knopf.dataset.hsText) knopf.dataset.hsText = knopf.innerHTML;
    knopf.disabled = true;
    knopf.setAttribute('aria-busy', 'true');
  }

  const daten = new FormData(form);
  const variante = String(daten.get('id'));
  const menge = Math.max(1, parseInt(String(daten.get('quantity')), 10) || 1);

  const abschnitte = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) abschnitte.push(el.dataset.sectionId);
  });
  if (abschnitte.length) daten.append('sections', abschnitte.join(','));

  const zugesagt = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variante, quantity: menge }],
      promise: zugesagt.promise,
    })
  );

  // Ali 22.10.2026 abends: Kachel ZYN Lemon Spritz zeigte „Fehler – bitte
  // erneut", nachgestellt lief derselbe Klick mit 200 durch. „Fehler" kommt
  // nur, wenn der Aufruf ohne Antwort abreisst (kein 429 -- das waere „Bitte
  // gleich erneut"), etwa „Failed to fetch" im fetch-Umbau von G:Volume. Dann
  // erst im Warenkorb nachsehen (vielleicht ist er doch durchgegangen), sonst
  // einmal selbst neu senden, bevor der Knopf einen Fehler meldet.
  // Die Menge davor wird abgewartet, nicht parallel geholt: liefe sie neben
  // dem Hinzufuegen, koennte sie die neuen Dosen schon mitzaehlen, und der
  // Knopf saehe „nicht angekommen" und sendete doppelt.
  const versuchen = async () => {
    const davor = await mengeImWarenkorb(variante);
    try {
      return await hinzufuegen(daten, knopf);
    } catch (err) {
      const jetzt = await mengeImWarenkorb(variante);
      if (davor === null || jetzt === null) throw err;
      if (jetzt > davor) return {};
      console.warn('[hs-warenkorb] erster Versuch fehlgeschlagen, sende neu:', err);
      await warte(NACHFASSEN_MS[0]);
      return hinzufuegen(daten, knopf);
    }
  };

  versuchen()
    .then(async (antwort) => {
      const fehler = Boolean(antwort.status);
      // Der Artikel liegt jetzt (oder eben nicht) im Warenkorb. Scheitert nur
      // das Nachladen -- z. B. /cart.json mit derselben 429 --, darf der Knopf
      // trotzdem nicht „Fehler" melden: wer dann nochmal klickt, hat zwei.
      let cart = null;
      try {
        cart = await warenkorbLaden();
      } catch (err) {
        console.warn('[hs-warenkorb] Warenkorb nicht nachgeladen:', err);
      }
      if (cart) {
        zugesagt.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: fehler,
            items: cart.items,
            source: 'hs-warenkorb',
            itemCount: menge,
            sections: fehler ? undefined : antwort.sections,
          },
        });
      } else {
        zugesagt.reject(new Error('Warenkorb nicht nachgeladen'));
      }
      // Shopify meldet z. B. „Du kannst nur 12 hinzufuegen" -- die Meldung
      // selbst ist genauer als jeder eigene Text.
      knopfZeigen(knopf, fehler ? antwort.description || antwort.message || 'Nicht möglich' : 'Hinzugefügt ✓', fehler);
    })
    .catch((err) => {
      // Vorher stumm -- darum war die 429 nur im Netzwerk-Tab zu sehen.
      console.warn('[hs-warenkorb] In den Warenkorb fehlgeschlagen:', err);
      zugesagt.reject(err);
      knopfZeigen(knopf, err?.status === 429 ? 'Bitte gleich erneut' : 'Fehler – bitte erneut', true);
    });
});
